import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import Masonry from 'react-masonry-css';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../contexts/CategoryContext';
import { usePlans } from '../contexts/PlanContext';
import { CategoryGroup } from './CategoryGroup';
import { CategoryModal } from './CategoryModal';
import { SubCategoryModal, SubCategoryFormData } from './SubCategoryModal';
import { CategoryDeleteModal } from './CategoryDeleteModal';
import { CategoryTransactionWarningModal } from './CategoryTransactionWarningModal';
import { CategoryWithMonthData } from '../types/category';
import { BudgetStatus } from './BudgetStatus';
import { PixelHourglass } from './PixelHourglass';

export const CategoryList: React.FC = () => {
  const { t } = useTranslation();
  const {
    categories,
    loading,
    budgetStatus, // PERFORMANCE: Get budget status from context (already fetched with categories)
    createCategory,
    updateCategory,
    updateCategoryMonth,
    updateCategoryPositions,
    checkCategoryTransactions,
    deleteCategory,
    refreshCategories,
  } = useCategories();
  const { currentPlan, currentMonthPlan } = usePlans();

  // Modal states
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isSubCategoryModalOpen, setIsSubCategoryModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTransactionWarningModalOpen, setIsTransactionWarningModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<CategoryWithMonthData | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<CategoryWithMonthData | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<CategoryWithMonthData | null>(null);
  const [transactionCount, setTransactionCount] = useState(0);

  // Track expanded/collapsed state for each group (by ID)
  // Load initial state from localStorage
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const stored = localStorage.getItem('categoryExpandedGroups');
    console.log('[CategoryList] Loading from localStorage:', stored);
    if (stored) {
      try {
        const parsed = new Set<string>(JSON.parse(stored) as string[]);
        console.log('[CategoryList] Parsed expanded groups:', Array.from(parsed));
        return parsed;
      } catch {
        console.log('[CategoryList] Failed to parse, using empty set');
        return new Set<string>();
      }
    }
    console.log('[CategoryList] No stored data, using empty set');
    return new Set<string>();
  });

  // Track if we've initialized - use ref to avoid re-renders
  const hasInitializedRef = useRef(false);
  const previousCategoryIdsRef = useRef<string>('');

  // Memoize group IDs to detect actual changes (not just category object reference changes)
  const groupIds = useMemo(() => categories.map(cat => cat.id).sort().join(','), [categories]);

  // Persist expanded state to localStorage whenever it changes
  useEffect(() => {
    if (hasInitializedRef.current) {
      const expandedArray = Array.from(expandedGroups);
      console.log('[CategoryList] Saving to localStorage:', expandedArray);
      localStorage.setItem('categoryExpandedGroups', JSON.stringify(expandedArray));
    }
  }, [expandedGroups]);

  // PERFORMANCE: Removed separate budget status fetch - now comes from context with categories

  // Initialize expanded state and handle new groups
  useEffect(() => {
    if (categories.length === 0) return;

    // Only run if category IDs actually changed
    if (previousCategoryIdsRef.current === groupIds) return;
    previousCategoryIdsRef.current = groupIds;

    setExpandedGroups(prev => {
      const currentGroupIds = new Set(categories.map(cat => cat.id));

      // First time initialization - check if localStorage had data
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        const stored = localStorage.getItem('categoryExpandedGroups');

        // If no stored data, expand all groups by default
        if (!stored) {
          return currentGroupIds;
        }

        // If we have stored data, use it (already loaded in useState)
        return prev;
      }

      const next = new Set<string>();

      // Keep expanded state for existing groups
      prev.forEach(id => {
        if (currentGroupIds.has(id)) {
          next.add(id);
        }
      });

      // Add new groups as expanded (but don't touch existing ones)
      categories.forEach(cat => {
        if (!prev.has(cat.id) && !next.has(cat.id)) {
          next.add(cat.id);
        }
      });

      return next;
    });
  }, [groupIds, categories]);

  // PERFORMANCE: Memoize toggle function to prevent CategoryGroup re-renders
  const toggleGroupExpansion = useCallback((groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
        console.log('[CategoryList] Collapsed group:', groupId, 'Remaining expanded:', Array.from(next));
      } else {
        next.add(groupId);
        console.log('[CategoryList] Expanded group:', groupId, 'All expanded:', Array.from(next));
      }
      return next;
    });
  }, []);

  // Drag and drop handlers - context handles optimistic UI
  const handleDragEnd = useCallback((result: DropResult) => {
    console.log('[DEBUG] handleDragEnd called with result:', result);
    const { source, destination, type } = result;

    // Dropped outside the list
    if (!destination) {
      console.log('[DEBUG] Dropped outside list, ignoring');
      return;
    }

    // No movement
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      console.log('[DEBUG] No movement detected, ignoring');
      return;
    }

    console.log('[DEBUG] Processing drag type:', type);

    if (type === 'group') {
      // Reordering groups
      const reordered = Array.from(categories);
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);

      // Build position updates
      const updates = reordered.map((group, index) => ({
        category_id: group.id,
        position: index,
      }));

      console.log('[DEBUG] Sending group position updates:', updates);

      // Context handles optimistic update and rollback
      updateCategoryPositions({ updates }, reordered).catch((error) => {
        console.error('Failed to save category positions:', error);
      });
    } else if (type.startsWith('subcategory-')) {
      // Reordering subcategories within a group
      const groupId = source.droppableId.replace('subcategories-', '');
      const reordered = Array.from(categories);
      const groupIndex = reordered.findIndex(g => g.id === groupId);

      if (groupIndex === -1) return;

      const group = reordered[groupIndex];
      const newChildren = Array.from(group.children);
      const [removed] = newChildren.splice(source.index, 1);
      newChildren.splice(destination.index, 0, removed);

      // Update group with new children order
      reordered[groupIndex] = { ...group, children: newChildren };

      // Build position updates for children only
      const updates = newChildren.map((child, index) => ({
        category_id: child.id,
        position: index,
      }));

      console.log('[DEBUG] Sending subcategory position updates:', updates);

      // Context handles optimistic update and rollback
      updateCategoryPositions({ updates }, reordered).catch((error) => {
        console.error('Failed to save category positions:', error);
      });
    }
  }, [categories, updateCategoryPositions]);

  // Handlers for Add Group
  const handleAddGroup = () => {
    setSelectedGroup(null);
    setIsGroupModalOpen(true);
  };
  const handleEditGroup = useCallback((group: CategoryWithMonthData) => {
    setSelectedGroup(group);
    setIsGroupModalOpen(true);
  }, []);

  const handleSaveGroup = async (name: string) => {
    if (!currentPlan || !currentMonthPlan) throw new Error('No active plan or month plan');

    if (selectedGroup) {
      // Update existing group
      await updateCategory(selectedGroup.id, { name });
    } else {
      // Create new group (for current + future months only)
      console.log('[DEBUG] Creating category with currentMonthPlan:', currentMonthPlan);
      await createCategory({
        name,
        plan_id: currentPlan.id,
        current_month_plan_id: currentMonthPlan.id,
        parent_id: null,
        is_installment: false,
      });
    }
  };

  // PERFORMANCE: Memoize handlers to prevent CategoryGroup re-renders
  // Handlers for Sub-Category
  const handleAddSubCategory = useCallback((group: CategoryWithMonthData) => {
    setSelectedGroup(group);
    setSelectedSubCategory(null);
    setIsSubCategoryModalOpen(true);
  }, []);

  const handleEditSubCategory = useCallback((group: CategoryWithMonthData, subCategory: CategoryWithMonthData) => {
    setSelectedGroup(group);
    setSelectedSubCategory(subCategory);
    setIsSubCategoryModalOpen(true);
  }, []);

  const handleSaveSubCategory = async (formData: SubCategoryFormData) => {
    if (!currentPlan || !currentMonthPlan) throw new Error('No active plan or month plan');

    if (selectedSubCategory) {
      // Update existing sub-category
      // Category table: name and is_installment flag only
      await updateCategory(selectedSubCategory.id, {
        name: formData.name,
        is_installment: formData.is_installment,
      });

      // Category_month table: allocation, same_allocation, installment progress/term, and goal fields
      // Installment fields and goal fields are now month-specific
      if (selectedSubCategory.category_month_id) {
        await updateCategoryMonth(selectedSubCategory.category_month_id, {
          allocated: formData.allocated,
          same_allocation: formData.same_allocation,
          installment_progress: formData.installment_progress,
          installment_term: formData.installment_term,

          // Goal-based saving fields
          is_goal: formData.is_goal,
          goal_target: formData.goal_target,
          goal_target_month: formData.goal_target_month,
        });
      }
    } else {
      // Create new sub-category
      if (!selectedGroup) throw new Error('No group selected');

      // Create category with initial allocation (backend creates category_month entries automatically)
      console.log('[DEBUG] Creating subcategory with currentMonthPlan:', currentMonthPlan);
      await createCategory({
        name: formData.name,
        plan_id: currentPlan.id,
        current_month_plan_id: currentMonthPlan.id,
        parent_id: selectedGroup.id,
        is_installment: formData.is_installment,
        initial_installment_term: formData.installment_term,
        initial_installment_progress: formData.installment_progress,
        initial_allocated: formData.allocated,
        initial_same_allocation: formData.same_allocation,

        // Goal-based saving fields
        initial_is_goal: formData.is_goal,
        initial_goal_target: formData.goal_target,
        initial_goal_target_month: formData.goal_target_month,
        initial_goal_monthly_plan: formData.goal_monthly_plan,
      });
    }

    // Refresh categories to update UI with goal field changes
    await refreshCategories();
  };

  // PERFORMANCE: Memoize delete handlers to prevent CategoryGroup re-renders
  const handleDeleteGroup = useCallback(async (group: CategoryWithMonthData) => {
    if (!currentMonthPlan) return;

    setCategoryToDelete(group);

    // Check for transactions first
    try {
      const result = await checkCategoryTransactions(group.id, currentMonthPlan.id);

      if (result.hasTransactions) {
        // Show transaction warning modal
        setTransactionCount(result.transactionCount);
        setIsTransactionWarningModalOpen(true);
      } else {
        // Show delete confirmation modal
        setIsDeleteModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to check transactions:', error);
    }
  }, [currentMonthPlan, checkCategoryTransactions]);

  const handleDeleteSubCategory = useCallback(async (subCategory: CategoryWithMonthData) => {
    if (!currentMonthPlan) return;

    setCategoryToDelete(subCategory);

    // Check for transactions first
    try {
      const result = await checkCategoryTransactions(subCategory.id, currentMonthPlan.id);

      if (result.hasTransactions) {
        // Show transaction warning modal
        setTransactionCount(result.transactionCount);
        setIsTransactionWarningModalOpen(true);
      } else {
        // Show delete confirmation modal
        setIsDeleteModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to check transactions:', error);
    }
  }, [currentMonthPlan, checkCategoryTransactions]);

  const handleConfirmDelete = async () => {
    if (!categoryToDelete || !currentMonthPlan) return;
    console.log('[DEBUG] Deleting category with currentMonthPlan:', currentMonthPlan);
    await deleteCategory(categoryToDelete.id, currentMonthPlan.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <PixelHourglass size="medium" speed="fast" color="keppel" data-id="budget-status-loader" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Budget Status - Using reusable component */}
      {budgetStatus && (
        <BudgetStatus
          status={budgetStatus.status}
          unallocated_amount={budgetStatus.unallocated_amount}
          underfunded_amount={budgetStatus.underfunded_amount}
        />
      )}

      {/* Categories */}
      {categories.length === 0 ? (
        <div
          className="bg-white border-4 border-[#077f5d] p-8"
          style={{ boxShadow: '6px 6px 0 #077f5d' }}
        >
          <div className="text-center">
            <div className="max-w-md mx-auto">
              <h3
                className="text-lg text-[#077f5d] mb-4 uppercase"
                style={{ fontFamily: "'Press Start 2P', monospace", lineHeight: '1.4' }}
              >
                {t('budget.no_category_title')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('budget.no_category_subtitle')}
              </p>
              <button
                onClick={handleAddGroup}
                className="w-full bg-[#3cbe8c] text-white px-6 py-3 border-2 border-[#077f5d] font-bold uppercase"
                style={{
                  fontFamily: "'Press Start 2P', monospace",
                  fontSize: '10px',
                  boxShadow: '6px 6px 0 #077f5d'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                  e.currentTarget.style.transform = 'translate(-2px, -2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
                  e.currentTarget.style.transform = 'translate(0, 0)';
                }}
              >
                <Plus
                  className="inline mr-2 mb-1 text-white"
                  size={14}
                  strokeWidth={3}
                />
                {t('budget.add_group')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="groups" type="group">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                <Masonry
                  breakpointCols={{
                    default: 3,
                    1024: 2,
                    640: 1
                  }}
                  className="masonry-grid"
                  columnClassName="masonry-grid-column"
                >
                  {categories.map((group, index) => (
                    <Draggable
                      key={group.id}
                      draggableId={group.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <CategoryGroup
                            category={group}
                            isExpanded={expandedGroups.has(group.id)}
                            onToggleExpand={() => toggleGroupExpansion(group.id)}
                            onAddSubCategory={() => handleAddSubCategory(group)}
                            onEditGroup={() => handleEditGroup(group)}
                            onEditSubCategory={(sub) => handleEditSubCategory(group, sub)}
                            onDeleteGroup={() => handleDeleteGroup(group)}
                            onDeleteSubCategory={handleDeleteSubCategory}
                            dragHandleProps={provided.dragHandleProps}
                            isDragging={snapshot.isDragging}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                </Masonry>
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Modals */}
      <CategoryModal
        isOpen={isGroupModalOpen}
        onClose={() => setIsGroupModalOpen(false)}
        onSave={handleSaveGroup}
        category={selectedGroup}
        title={selectedGroup ? t('budget.edit_group_modal_title') : t('budget.add_group_modal_title')}
      />

      <SubCategoryModal
        isOpen={isSubCategoryModalOpen}
        onClose={() => setIsSubCategoryModalOpen(false)}
        onSave={handleSaveSubCategory}
        category={selectedSubCategory}
        title={selectedSubCategory ? t('budget.edit_category_modal_title') : t('budget.add_category_modal_title')}
      />

      <CategoryDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        categoryName={categoryToDelete?.name || ''}
        hasChildren={categoryToDelete?.is_parent && categoryToDelete.children.length > 0}
      />

      <CategoryTransactionWarningModal
        isOpen={isTransactionWarningModalOpen}
        onClose={() => setIsTransactionWarningModalOpen(false)}
        categoryName={categoryToDelete?.name || ''}
        categoryId={categoryToDelete?.id || ''}
        isParent={categoryToDelete?.is_parent || false}
        transactionCount={transactionCount}
      />
    </div>
  );
};
